import { sql } from "drizzle-orm";
import {
  boolean,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { endUsers } from "../app";
import { tenant } from "../auth";

export const orderTypeEnum = pgEnum("order_type", [
  "TCG",
  "MARKETPLACE",
  "WITHDRAW_ASSET",
  "WITHDRAW_STEAM",
  "SELL_FOR_CRYPTO",
  "EXTERNAL",
]);

export const fulfillmentMethodEnum = pgEnum("fulfillment_method", [
  "FACILITY_SHIP",
  "PARTNER_DROPSHIP",
  /** A human buys the item on the vendor's site from the dashboard: every vendor but Fanatics. */
  "MANUAL_BUY",
  /** The same buy driven by a bot, through a FANATICS_BUY job on the Job API. */
  "BUY_BOT",
  "CRYPTO_TX",
  "STEAM_TRADE",
  /** The merchant fulfills it in Shopify (a claimed Shopify box unit); never a warehouse job. */
  "SHOPIFY_ORDER",
]);

export const orderStatusEnum = pgEnum("order_status", [
  "PENDING",
  "PROCESSING",
  "PRESHIPPED",
  "SHIPPED",
  "CANCELLED",
  "UNAPPROVED",
  "APPROVED",
  "HOLD",
  "SUCCESS",
  "FAIL",
]);

/**
 * Why a cancel happened, for a tenant reading the order back. Stored beside the
 * free-text `cancellation_reason` so a note that happens to contain a code is
 * still just a note. Null means nobody recorded a cause (every migrated boxed row).
 *
 * The code also decides which statuses the cancel may act from - see
 * `canCancelOrderFrom` in `fulfillment-core/order/state-machine`. An end user may
 * only cancel an order that has not been prepared yet; an operator may cancel one
 * that has already shipped.
 */
export enum OrderCancellationCode {
  /** The vendor's live buy price was above the snapshot - cancel on ANY excess, no discretion band. */
  PRICE_EXCEEDED = "PRICE_EXCEEDED",
  /** The item, or the variant the user picked, was gone from the vendor before the fulfiller bought it. */
  OUT_OF_STOCK = "OUT_OF_STOCK",
  /**
   * The tenant's own end user cancelled, through the tenant's API.
   *
   * Narrowed when TENANT_ADMIN_CANCELLED was added: rows written before that carry this code for
   * operator cancels too, so a split by initiator only holds from that deploy forward.
   */
  TENANT_CANCELLED = "TENANT_CANCELLED",
  /** The tenant's own operator cancelled on a user's behalf - not loot-os platform staff. */
  TENANT_ADMIN_CANCELLED = "TENANT_ADMIN_CANCELLED",
}

export const orderCancellationCodeEnum = pgEnum("order_cancellation_code", [
  OrderCancellationCode.PRICE_EXCEEDED,
  OrderCancellationCode.OUT_OF_STOCK,
  OrderCancellationCode.TENANT_CANCELLED,
  OrderCancellationCode.TENANT_ADMIN_CANCELLED,
]);

export const orders = pgTable(
  "orders",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    /** The tenant that placed the order. Orders are operations records, so no cascade. */
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id),
    /** The end user the order is for. The account sync creates the row before the first order. */
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id),
    type: orderTypeEnum("type").notNull(),
    vendorId: uuid("vendor_id"),
    method: fulfillmentMethodEnum("method"),
    status: orderStatusEnum("status").notNull(),
    paidWith: text("paid_with"),
    shippingAddress: jsonb("shipping_address"),
    shippingFee: integer("shipping_fee").notNull().default(0),
    shippingFeeRefunded: boolean("shipping_fee_refunded").notNull().default(false),
    totalCost: integer("total_cost"),
    trackingNumber: text("tracking_number"),
    /** The tenant's own reference for this order. Unique per tenant, which is what makes createOrder idempotent. */
    tenantReferenceId: text("tenant_reference_id"),
    details: jsonb("details"),
    marketplaceTxId: text("marketplace_tx_id"),
    consumablesUsed: text("consumables_used").array().notNull().default([]),
    notes: text("notes"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
    processingAt: timestamp("processing_at", { withTimezone: true }),
    preshippedAt: timestamp("preshipped_at", { withTimezone: true }),
    shippedAt: timestamp("shipped_at", { withTimezone: true }),
    cancelledAt: timestamp("cancelled_at", { withTimezone: true }),
    cancellationReason: text("cancellation_reason"),
    cancellationCode: orderCancellationCodeEnum("cancellation_code"),
    /**
     * Platform staff's choices on a dashboard cancel (`CancellationPlan` in fulfillment-core,
     * read only through `parseCancellationPlan`): the customer's message, whether to email them,
     * a marketplace bonus. The tenant that owns the order reads it back and applies it on its own
     * side. Null on every other cancel, and on an order not cancelled.
     */
    cancellationPlan: jsonb("cancellation_plan"),
    /**
     * Platform staff's choices on a dashboard ship (`ShipmentPlan` in fulfillment-core, read only
     * through `parseShipmentPlan`): whether the tenant emails the customer. The tenant that owns
     * the order reads it back and applies it on its own side. Null on every other ship.
     */
    shipmentPlan: jsonb("shipment_plan"),
  },
  (t) => [
    uniqueIndex("orders_tenant_reference_uidx")
      .on(t.tenantId, t.tenantReferenceId)
      .where(sql`${t.tenantReferenceId} is not null`),
    index("orders_tenant_end_user_created_idx").on(t.tenantId, t.endUserId, t.createdAt),
    // The crons and the staff search read orders by status, newest first: the hourly pending
    // advance (PENDING) and the fulfillment console's status filters.
    index("orders_status_created_idx").on(t.status, t.createdAt),
    // The staff order list's account lookup pages one end user's orders in any tenant, newest
    // first, by (created_at, id); the tenant index above needs a tenant to reach end_user_id.
    index("orders_end_user_created_idx").on(t.endUserId, t.createdAt, t.id),
    // The catch-up feed, `GET /orders/changes`, pages a tenant's orders by (updated_at, id).
    index("orders_tenant_updated_idx").on(t.tenantId, t.updatedAt, t.id),
  ],
);
